/**
 * フロントエンド本体（vanilla TS, esbuild で public/assets/app.js へバンドル）。
 * Orbital Glass デザイン（ハンドオフ採用案）を5ページのハッシュルーティングで実装：
 *   #/           トップ（地図ヒーロー→次の打ち上げ→カレンダー→待ち時間の宇宙→3ステップ）
 *   #/launches   打ち上げ一覧（射場/確度フィルタ・月グルーピング・通知導線）
 *   #/arrange    手配ページ（選んだ1件の日程で宿・レンタカー・航空券・軌道）
 *   #/guide      射場ガイド（観覧スポット・アクセス・持ち物）
 *   #/about      このサイトについて
 * 射場マスタ(launch-sites.ts)をバンドルに同梱し、APIが落ちても静的射場情報で動く。
 */
import type { LaunchDTO, LaunchesResponse, HotelsResponse } from './types';
import {
  LAUNCH_SITES,
  getSiteById,
  getGuideById,
  GLOBAL_CAVEAT,
  type LaunchSite,
} from './data/launch-sites';
import {
  formatLaunchDate,
  computeStay,
  statusInfo,
  escapeHtml as E,
  precisionInfo,
  jstDate,
  isoDate,
} from './lib/format';
import {
  todayIsoJst,
  addDaysIso,
  diffDays,
  clampDates,
  clampAdults,
  clampRooms,
  restoreDates,
  departRange,
  returnRange,
  nightsOf,
  labelJp,
  tripIssues,
  bookingWindowNotice,
  isBeyondBookingWindow,
  tripSummaryLabel,
  MIN_ADULTS,
  MAX_ADULTS,
  MIN_ROOMS,
  MAX_ROOMS,
  type TripConditions,
} from './lib/trip';
import {
  sortForSchedule,
  renderAnswerHtml,
  renderScheduleTableHtml,
  type LaunchSummaryItem,
} from './lib/launch-summary';
import { cacheAgeLevel } from './lib/launch-cache';
import { missionJa } from './data/mission-names-ja';
import {
  DEPARTURE_PREFECTURES,
  DEPARTURE_REGIONS,
  DEFAULT_PREF_CODE,
  findPrefByCode,
  findPrefByIata,
  resolveAirport,
  departureLabel,
  shortAirportName,
  type DeparturePrefecture,
  type DepartureAirport,
} from './data/departure-prefectures';
import { isKnownDeparturePref } from './lib/geo-pref';
import { rentacarLinks, flightLinks, otherHotelLinks, activityLinks, type DeepLink } from './lib/deeplinks';
import { decorate, isAffiliated, loadAffiliateConfig } from './lib/affiliate';
import { buildLaunchJsonLd, LD_SCRIPT_ID } from './lib/launch-block';
import { japanMapSvg, issDiagramSvg, orbitDiagramSvg, type MapPin } from './lib/svg';
import { HERO_PHOTO, getSitePhoto, type Photo } from './data/photos';

const AFFILIATE = loadAffiliateConfig();

// 問い合わせの送り先（フッターの「お問い合わせフォーム」だけがここを使う）。
const CONTACT_FORM =
  'https://docs.google.com/forms/d/e/1FAIpQLScIdeYIdK4h1qQuc7CNOO6Dzy9gm2nrbziYDtt7TiMLI0ohHw/viewform';

/**
 * 通知の受け口＝公式LINEの友だち追加URL（2026-08-11に配信を開通）。
 *
 * ★ここを唯一の出所にする。通知導線が5か所あり、以前はすべてGoogleフォームへ送っていたが、
 *   フォームには通知を送る仕組みが無い（メール配信基盤は未構築）ので「通知します」が嘘だった。
 * ⚠️ `@` は `%40` にエンコードする（公式仕様は `line.me/R/ti/p/` 形式。生の `@` だと開かないことがある）。
 * ⚠️ Channel ID（数字）ではなく**ベーシックID**（`@`+英数字）。Channel IDは友だち追加URLに使えない。
 */
const LINE_ADD_FRIEND = 'https://line.me/R/ti/p/%40189xaequ';

/** 観覧の持ち物・注意（射場共通の一般的な備え。射場固有の値ではない）。 */
const VIEWING_CHECKLIST = [
  '飲料と軽食（売店は混雑）',
  '帽子・日焼け対策（日陰なし）',
  '虫よけ・雨具',
  'モバイルバッテリー（電波が弱い）',
  '現金（一部の駐車・売店）',
  '耳栓（近距離は音圧が大きい）',
];

interface Route {
  page: 'top' | 'launches' | 'arrange' | 'guide' | 'about';
  query: URLSearchParams;
}

interface State {
  launches: LaunchDTO[];
  meta: Pick<LaunchesResponse, 'source' | 'stale' | 'cachedAt' | 'error'> | null;
  loaded: boolean;
  selectedLaunchId: string | null;
  selectedSiteId: string | null;
  pre: number;
  post: number;
  /** 出発地の都道府県コード（画面で選ぶのはこちら） */
  departurePref: string;
  /** 上で選ばれた県の中の空港。ディープリンクに渡すのはこの IATA */
  departureIata: string;
  /**
   * 出発日・帰着日の手動上書き。
   * ★どの打ち上げに対して入れた日付かを一緒に持つ。別の回を選び直したときに
   *   前の回の日付が残っていると、気づかないまま違う日程で外部サイトへ飛ぶ。
   *   launchId が一致しないときは無視して、打ち上げ日からの自動算出に戻す。
   */
  tripOverride: { launchId: string; departIso: string; returnIso: string } | null;
  /** 大人の人数（打ち上げをまたいで保持する＝利用者の属性であって旅程の属性ではない） */
  adults: number;
  /** 部屋数（宿のみ） */
  rooms: number;
  radiusKm: number;
  route: Route;
  filterSite: string; // 'all' | siteId
  filterConf: 'all' | 'confirmed';
}

const hotelCache = new Map<string, HotelsResponse>();
let hotelReqId = 0;
/** いま描画中の手配ページが「予約受付前」表示かどうか（null = 未描画）。境界をまたいだら作り直す。 */
let arrangeBeyondWindow: boolean | null = null;

/**
 * 保存済みの出発地を復元する。
 * 旧バージョンは IATA だけを `rvp.departure` に保存していたため、その値からも県を逆引きして拾う。
 *
 * `chosen` = 利用者が自分で選んだ値が残っているか。false のときだけ、あとから
 * /api/geo の推定（接続元IPの県）で上書きする。**選択は推定に絶対に上書きされない。**
 */
function initialDeparture(): { pref: string; iata: string; chosen: boolean } {
  const savedIata = (localStorage.getItem('rvp.departure') || '').toUpperCase();
  const saved = findPrefByCode(localStorage.getItem('rvp.departure.pref')) ?? findPrefByIata(savedIata);
  const pref = saved ?? findPrefByCode(DEFAULT_PREF_CODE)!;
  return { pref: pref.code, iata: resolveAirport(pref, savedIata).iata, chosen: !!saved };
}
const initialDep = initialDeparture();

/**
 * 出発地の出どころ。
 *  'default'  … まだ何も分からないので既定（東京）
 *  'geo'      … 接続元IPからの推定（画面に「現在地から推定」と出す）
 *  'chosen'   … 利用者が自分で選んだ（保存済み。推定で上書きしない）
 */
let departureOrigin: 'default' | 'geo' | 'chosen' = initialDep.chosen ? 'chosen' : 'default';

/**
 * 保存済みの旅程条件を復元する。
 * ★日付は「今日以降・1〜30泊」でなければ捨てる（restoreDates が判定）。
 *   前回の旅行の日付が残ったまま外部サイトへ飛ぶのが最悪の失敗なので、疑わしければ自動算出に戻す。
 */
function initialTripState(): Pick<State, 'tripOverride' | 'adults' | 'rooms'> {
  const today = todayIsoJst();
  const launchId = localStorage.getItem('rvp.trip.launch');
  const dates = restoreDates(localStorage.getItem('rvp.trip.depart'), localStorage.getItem('rvp.trip.return'), today);
  return {
    tripOverride: launchId && dates ? { launchId, ...dates } : null,
    adults: clampAdults(localStorage.getItem('rvp.trip.adults') ?? 2),
    rooms: clampRooms(localStorage.getItem('rvp.trip.rooms') ?? 1),
  };
}
const initialTrip = initialTripState();

const state: State = {
  launches: [],
  meta: null,
  loaded: false,
  selectedLaunchId: null,
  selectedSiteId: null,
  pre: 1,
  post: 1,
  departurePref: initialDep.pref,
  departureIata: initialDep.iata,
  tripOverride: initialTrip.tripOverride,
  adults: initialTrip.adults,
  rooms: initialTrip.rooms,
  radiusKm: 20,
  route: { page: 'top', query: new URLSearchParams() },
  filterSite: 'all',
  filterConf: 'all',
};

const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector<T>(sel);
const app = () => document.getElementById('app')!;
const toneClass = (t: string) => `tone-${t}`;

// ---- データの派生ヘルパー ---------------------------------------------------

function rocketOf(l: LaunchDTO): string {
  return l.rocket || l.name.split('|')[0]?.trim() || '';
}
function missionOf(l: LaunchDTO): string {
  return l.mission || l.name.split('|')[1]?.trim() || '';
}
function vehicleLabel(l: LaunchDTO): string {
  const r = rocketOf(l);
  const m = missionOf(l);
  return m ? `${r}｜${m}` : r || '—';
}
/**
 * 一覧の並び順。実体は src/lib/launch-summary.ts の sortForSchedule に集約している
 * （静的HTML生成側と実行時で並びを食い違わせないため）。
 *
 * ⚠️ 2026-07-31の是正: 以前は「確度の粗い回を無条件で末尾へ」送っていたため、
 *    2026年内のY精度5件（ETS-9 / HTV-X2 / HTV-X3 / LUPEX / LOTUSat-1）が
 *    2028年の回より後ろに回り、先頭4件から押し出されていた（実測）。
 *    年→確度→net の順に直し、粗い回の後回しは「同じ年の中でだけ」効かせる。
 */
function sortedLaunches(): LaunchDTO[] {
  return sortForSchedule(state.launches);
}
/** 指定射場の直近（最も早い）打ち上げ。 */
function nextLaunchForSite(siteId: string): LaunchDTO | null {
  return sortedLaunches().find((l) => l.siteId === siteId) ?? null;
}
/** 全体で最も早い打ち上げの射場id（トップの「次の打ち上げ」強調用）。 */
function globalNextSiteId(): string | null {
  return sortedLaunches().find((l) => l.siteId)?.siteId ?? null;
}
function firstBookable(): LaunchDTO | null {
  return sortedLaunches().find((l) => precisionInfo(l.netPrecision).bookable && l.siteId) ?? null;
}
function launchById(id: string | null): LaunchDTO | null {
  return id ? (state.launches.find((l) => l.id === id) ?? null) : null;
}
/** 残り日数（JST日付ベース。過去なら null）。 */
function daysUntil(netIso: string): number | null {
  const ymd = jstDate(netIso);
  if (!ymd) return null;
  const target = Date.UTC(ymd.y, ymd.m - 1, ymd.d);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const d = Math.round((target - today) / 86400000);
  return d;
}

// ---- データ取得 -------------------------------------------------------------

async function loadLaunches(): Promise<void> {
  try {
    const res = await fetch('/api/launches', { headers: { Accept: 'application/json' } });
    const data = (await res.json()) as LaunchesResponse;
    state.launches = Array.isArray(data.launches) ? data.launches : [];
    state.meta = { source: data.source, stale: data.stale, cachedAt: data.cachedAt, error: data.error };
  } catch (err) {
    state.launches = [];
    state.meta = { source: 'error', stale: true, cachedAt: new Date().toISOString(), error: String(err) };
  }
  state.loaded = true;
  // 既定選択：直近の予約可能な打ち上げ
  const fb = firstBookable();
  if (fb) {
    state.selectedLaunchId = fb.id;
    state.selectedSiteId = fb.siteId;
  }
  injectEventJsonLd();
  render();
}

/**
 * 打ち上げデータ由来の JSON-LD（WebPage の dateModified + 確定日付の Event）を更新する。
 *
 * ⚠️ 2026-08-04: 中身の組み立ては src/lib/launch-block.ts に集約した。
 *   素のHTMLにも同じ id の <script> が焼かれている（AIクローラはJSを実行しないため）ので、
 *   ここは**同じ要素を上書きする**。組み立てを二重に書くと、
 *   レンダ前とレンダ後で構造化データが食い違う（＝クローキング扱いになりうる）。
 */
function injectEventJsonLd(): void {
  try {
    const payload = buildLaunchJsonLd(
      state.launches,
      state.meta?.cachedAt ?? new Date().toISOString(),
      Date.now(),
    );
    let el = document.getElementById(LD_SCRIPT_ID) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = LD_SCRIPT_ID;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
  } catch {
    /* best-effort */
  }
}

// ---- 共通の小コンポーネント -------------------------------------------------

/** 確度バッジ（確定/年内/時期未定）。 */
function confBadge(l: LaunchDTO, sm = false): string {
  const p = precisionInfo(l.netPrecision);
  return `<span class="badge ${sm ? 'sm ' : ''}${toneClass(p.tone)}">${E(p.label)}</span>`;
}

/** 外部送客ディープリンクのカード群（全URLを decorate 経由でアフィリンク化）。 */
function deepLinkCards(links: DeepLink[]): string {
  return (
    `<div class="dl-grid">` +
    links
      .map((l) => {
        const href = decorate(l.serviceId, l.url, AFFILIATE);
        const rel = isAffiliated(l.serviceId, AFFILIATE)
          ? 'noopener noreferrer sponsored nofollow'
          : 'noopener noreferrer';
        // data-qa-service はアクセス解析（quirio-analytics）が読む。
        // ⚠️ decorate() が装飾済みリンクを全部 ck.jp.ap.valuecommerce.com か
        //    hb.afl.rakuten.co.jp に書き換えるので、href のホストでは広告主を判別できない
        //    ＝この属性が無いと「どこへ送客したか」が永久に分からない。
        //    送られるのはこの serviceId だけで、URL も日付も人数も送られない。
        return `
    <a class="dl-card glass-sub ${l.prefilled ? 'is-prefilled' : 'is-manual'}" href="${E(href)}" target="_blank" rel="${rel}" data-qa-service="${E(l.serviceId)}">
      <span class="dl-top"><span class="dl-label">${E(l.label)}</span>${l.prefilled ? '<span class="dl-badge">条件プリセット</span>' : '<span class="dl-badge manual">手動入力</span>'}</span>
      <span class="dl-note">${E(l.note)}</span>
    </a>`;
      })
      .join('') +
    `</div>`
  );
}

function adNote(scope: string): string {
  return `<p class="ad-note">※ ${E(scope)}のリンクには<b>広告（アフィリエイト）</b>を含む場合があります。<a href="/legal#operator-h">表示について</a></p>`;
}

/**
 * 写真＋クレジット。CC BY / CC BY-SA は著作者表示が利用条件なので、
 * クレジットは装飾ではなく**必須要素**として必ず一緒に描く。
 * `eager` を指定した1枚以外は遅延読み込み（初期表示の重量を増やさない）。
 */
function photoFigure(p: Photo, cls: string, eager = false): string {
  return `
  <figure class="photo ${cls}">
    <img src="/assets/photos/${E(p.file)}" alt="${E(p.alt)}" width="${p.width}" height="${p.height}"
      loading="${eager ? 'eager' : 'lazy'}" decoding="async"${eager ? ' fetchpriority="high"' : ''} />
    <figcaption class="photo-cap">
      <span class="pc-text">${E(p.caption)}</span>
      <span class="pc-credit">${E(p.author)} ／ <a href="${E(p.licenseUrl)}" target="_blank" rel="noopener noreferrer nofollow">${E(p.license)}</a> ／ <a href="${E(p.sourceUrl)}" target="_blank" rel="noopener noreferrer nofollow">出典</a></span>
    </figcaption>
  </figure>`;
}

/** 「通知を受け取る」導線。送り先は公式LINEの友だち追加（[[LINE_ADD_FRIEND]]）。 */
function notifyLink(label: string, cls: string): string {
  return `<a class="${cls}" href="${LINE_ADD_FRIEND}" target="_blank" rel="noopener noreferrer">${E(label)}</a>`;
}

// ============================================================================
// TOP ページ
// ============================================================================

function renderTop(): void {
  const root = app();
  const nextSiteId = globalNextSiteId();

  const heroMap = `
    <section class="top-grid">
      <div class="map-hero glass-hero">
        <span class="map-legend"><span class="dot"></span>打ち上げ予定のある射場</span>
        <span class="map-corner eyebrow">JAPAN · 4 SITES</span>
        ${topMapSvg(nextSiteId)}
      </div>
      <div>
        <div class="site-list-head">
          <h2 class="section-h">射場マップ</h2>
          <span class="ctx">ピンを選ぶと日程が決まる</span>
        </div>
        <div class="site-list">${LAUNCH_SITES.map((s, i) => siteRow(s, i + 1, nextSiteId)).join('')}</div>
        <div class="info-panel" style="padding:16px 18px;margin-top:16px">
          <div class="card-h" style="margin-bottom:6px">日付が仮の回について</div>
          <p class="ctx">「年内」「時期未定」の回は宿の予約リンクを開かず、日程が確定したときの通知登録に切り替わります。</p>
        </div>
      </div>
    </section>`;

  root.innerHTML = `
    ${bannerHtml()}
    <section class="hero hero-split">
      <div class="hero-copy">
        <p class="eyebrow-lg">JAPAN LAUNCH CALENDAR · 2026</p>
        <!-- <wbr>＝折り返し可能位置のみを示し文字は増やさない（textContentは検索語のまま）。
             word-break:auto-phrase だと見出しの1フレーズが分割不能で狭い画面ではみ出すため。
             index.html の静的初期HTMLと同じ位置に入れている。 -->
        <h1 class="hero-title">国内ロケット<wbr />打ち上げ予定カレンダー<br /><span class="down">次の打ち上げはいつ？ 見に行くところまで。</span></h1>
        <p class="hero-lede">日本国内のロケット打ち上げ予定をまとめた無料カレンダーです。種子島宇宙センター・内之浦宇宙空間観測所・スペースポート紀伊・北海道スペースポートの4射場について、次の打ち上げ日時を確度つきで一覧できます。打ち上げを1件選ぶだけで、その日程・その地域の宿・レンタカー・航空券の手配情報がまとまります。</p>
        <div class="hero-cta-row">
          <a class="btn btn-primary" href="#/launches">打ち上げを選ぶ</a>
          <a class="btn btn-glass" href="#/about">このサイトについて</a>
        </div>
      </div>
      ${photoFigure(HERO_PHOTO, 'photo-hero', true)}
    </section>
    ${heroMap}
    ${topCalendar()}
    ${topSpaceSection()}
    ${topSteps()}
    ${topSiteGuideLinks()}
    ${topFaq()}
    ${topAbout()}
  `;
}

/**
 * 射場ガイド（実URLの静的ページ）への本文内リンク。
 * ★SEO上の役割: 上の site-list は SPA の選択操作なので <button>＝クローラは辿れない。
 *   /sites/<slug>/ という独自コンテンツ4枚へ、説明文つきの <a> で本文から確実に繋ぐ。
 * ★index.html の静的初期HTMLにも同じ4件を置いてあり、文言をそろえている。
 */
function topSiteGuideLinks(): string {
  const cards: Array<[string, string, string]> = [
    [
      'tanegashima',
      '種子島宇宙センター（鹿児島県南種子町）',
      'JAXA・三菱重工が運用する日本最大の射場。H3ロケットの打ち上げを南種子町管理の4公園から観覧します。',
    ],
    [
      'uchinoura',
      '内之浦宇宙空間観測所（鹿児島県肝付町）',
      'JAXAの固体ロケット射場。イプシロンS等の打ち上げに対応し、丘陵地の観覧場所から見上げる構図になります。',
    ],
    [
      'spaceport-kii',
      'スペースポート紀伊（和歌山県串本町）',
      'スペースワンが運用する民間小型ロケット「カイロス」の射場。本州にあり、鉄道と車でのアクセスが現実的です。',
    ],
    [
      'hokkaido-spaceport',
      '北海道スペースポート（北海道大樹町）',
      'SPACE COTANが運営。インターステラテクノロジズの ZERO 等を想定した、北海道・十勝の民間射場です。',
    ],
  ];
  return `
  <section class="section">
    <h2 class="section-h">日本国内のロケット打ち上げ射場<span class="section-note">4か所すべてに観覧ガイドがあります。</span></h2>
    <div class="about-grid" style="margin-top:16px">
      ${cards
        .map(
          ([slug, name, memo]) =>
            `<div class="glass-sub"><h3><a href="/sites/${slug}/">${name}</a></h3><p>${memo}</p></div>`,
        )
        .join('')}
    </div>
  </section>`;
}

/**
 * よくある質問。
 * ★index.html の静的初期HTML・<head> の FAQPage 構造化データと**文面を一致させること**。
 *   3者が食い違うとリッチリザルトの手動対策対象になる（構造化データは本文の再掲でなければならない）。
 */
const FAQ_ITEMS: Array<[string, string]> = [
  [
    '次の国内ロケット打ち上げはいつですか？',
    'トップページの打ち上げカレンダーに、直近の国内打ち上げ予定を日付順で表示しています。日時には「確定・日付のみ・月のみ・年内」の確度バッジが付き、確度が足りない回は宿の予約リンクを開かず通知登録に切り替わります。日本の打ち上げは延期が頻発するため、最終的な確定日は各運用機関の公式ページでご確認ください。',
  ],
  [
    '日本国内のロケット打ち上げ射場はどこにありますか？',
    '当サイトが対応するのは4か所です。種子島宇宙センター（鹿児島県南種子町・JAXA / 三菱重工）、内之浦宇宙空間観測所（鹿児島県肝付町・JAXA）、スペースポート紀伊（和歌山県串本町・スペースワン）、北海道スペースポート（北海道大樹町・SPACE COTAN）。それぞれの観覧スポットとアクセスは射場ガイドにまとめています。',
  ],
  [
    'ロケットの打ち上げは誰でも見学できますか？',
    '射場そのものは打ち上げ時に立入禁止になりますが、周辺に自治体などが用意する観覧場所があります。たとえば種子島では南種子町が管理する4つの公園が観覧場となり、最も射点に近い恵美之江展望公園だけが事前申込制です。射場ごとの観覧スポットと予約要否は射場ガイドに記載しています。',
  ],
  [
    '打ち上げの日程はどのくらい変わりますか？',
    '天候・技術要因による延期（スクラブ）が頻発し、予備期間が数週間に及ぶこともあります。宿・レンタカー・航空便は予備日を2〜3日みて確保するのが安全です。当サイトは日程が仮の回では予約リンクを開かず、日程が確定したときの通知登録に切り替わります。',
  ],
  [
    'このカレンダーのデータはどこから取得していますか？',
    '打ち上げ予定は The Space Devs の Launch Library 2、射場周辺のホテルの空室・料金は楽天トラベルのAPIから取得しています。いずれも取得時点の参考値であり、確定情報は各公式サイトでご確認ください。',
  ],
  [
    '利用料金はかかりますか？',
    '無料です。当サイトは予約を代行せず、楽天トラベル等の各予約サイトへ送客します。リンク経由で予約があった場合に手数料を受け取ることがありますが、利用者の支払額が増えることはありません。',
  ],
];

function topFaq(): string {
  return `
  <section class="section">
    <h2 class="section-h">よくある質問</h2>
    <div class="faq-list">
      ${FAQ_ITEMS.map(
        ([q, a], i) => `<details class="faq-item glass-sub"${i === 0 ? ' open' : ''}>
        <summary><h3>${E(q)}</h3></summary>
        <p>${E(a)}</p>
      </details>`,
      ).join('')}
    </div>
  </section>`;
}

function topMapSvg(nextSiteId: string | null): string {
  const pins: MapPin[] = LAUNCH_SITES.map((s, i) => ({
    id: s.id,
    num: i + 1,
    selected: state.selectedSiteId === s.id,
    isNext: s.id === nextSiteId,
  }));
  return japanMapSvg(pins);
}

function siteRow(s: LaunchSite, num: number, nextSiteId: string | null): string {
  const l = nextLaunchForSite(s.id);
  const isNext = s.id === nextSiteId;
  const conf = l ? precisionInfo(l.netPrecision) : null;
  const { period, color } = shortPeriod(l);
  return `
  <button class="site-row glass-sub ${isNext ? 'is-next' : ''} ${state.selectedSiteId === s.id ? 'is-selected' : ''}"
    data-action="select-site" data-site="${E(s.id)}">
    <span class="num-badge">${num}</span>
    <span class="s-body">
      ${isNext ? '<span class="next-tag">次の打ち上げ</span>' : ''}
      <span class="s-name">${E(s.nameJa.replace(/（.*/, ''))}</span>
      <span class="s-mission">${l ? E(vehicleLabel(l)) : '予定は未定'}</span>
    </span>
    <span class="s-right">
      <span class="s-date" style="color:${color}">${E(period)}</span>
      ${conf && conf.bookable ? `<span class="s-conf ok-txt">確定</span>` : ''}
    </span>
  </button>`;
}

/** サイトリスト右の期間表示＋色（確定=月.日/青 · 仮=月/濃 · 年内=琥珀 · 未定=灰）。 */
function shortPeriod(l: LaunchDTO | null): { period: string; color: string } {
  if (!l) return { period: '—', color: 'var(--faint)' };
  const t = precisionInfo(l.netPrecision).tier;
  const ymd = jstDate(l.net);
  if (ymd && (t === 'time' || t === 'day')) return { period: `${ymd.m}.${ymd.d}`, color: 'var(--brand)' };
  if (ymd && (t === 'week' || t === 'month' || t === 'quarter')) return { period: `${ymd.m}月`, color: 'var(--ink-3)' };
  if (t === 'year') return { period: '年内', color: 'var(--warn-fg)' };
  return { period: '未定', color: 'var(--faint)' };
}

/** 射場idから一覧表示用の短い名前を引く（共有レンダラに渡す解決関数）。 */
const siteLabelResolver = (id: string | null) => {
  const s = id ? getSiteById(id) : undefined;
  return s ? { short: s.nameJa.replace(/（.*/, '') } : null;
};

/**
 * トップの「次の打ち上げ」直答＋予定一覧。
 *
 * ⚠️ ここは src/lib/launch-summary.ts の共有レンダラを通す。
 *    index.html の静的初期HTMLも**同じ関数**でビルド時に生成しているため、
 *    JS実行前後で中身が食い違わない（クローキング回避の実装上の担保）。
 *    以前は sortedLaunches().slice(0,4) で先頭4件だけ出しており、
 *    静的HTMLには一覧が1行も無かった。
 */
function topCalendar(): string {
  if (!state.loaded) return `<section class="section"><p class="loading">打ち上げ情報を読み込み中…</p></section>`;
  const list = state.launches as LaunchSummaryItem[];
  const capturedAt = state.meta?.cachedAt ?? new Date().toISOString();
  const jaMission = (l: LaunchSummaryItem) =>
    missionJa(l.mission ?? l.name.split('|')[1]?.trim() ?? null, l.rocket ?? l.name.split('|')[0]?.trim() ?? null);
  return (
    renderAnswerHtml(list, capturedAt, siteLabelResolver, jaMission) +
    renderScheduleTableHtml(list, capturedAt, siteLabelResolver, { jaMission }) +
    `<p class="ctx" style="margin-top:-8px"><a class="link-arrow" href="#/launches">確度バッジ付きの一覧・手配へ進む →</a></p>`
  );
}

// ※ calRow() は topCalendar が共有レンダラ（src/lib/launch-summary.ts）に移行したことで
//   参照が無くなったため削除した。打ち上げ一覧ページ側は launchRow() を使っている。

function topSpaceSection(): string {
  return `
  <section class="section">
    <h2 class="section-h">待ち時間に見える宇宙<span class="section-note">ISSと衛星の可視パス。前夜と翌朝の空も、現地で見る価値のある対象です。</span></h2>
    <div class="space-grid" style="margin-top:16px">
      <div class="space-card glass">
        <div class="sc-head"><span class="iss-tag">ISS</span><span class="sc-title">前夜・翌朝はISSが見える</span></div>
        <div class="iss-body">
          ${issDiagramSvg()}
          <p class="iss-lede">打ち上げの前夜や翌朝は、条件が合えばISS（国際宇宙ステーション）が数分かけて空を横切ります。正確な通過時刻は観測地・日付ごとに変わるため、日程が決まったら公式LINEでお知らせします。</p>
        </div>
        <div style="margin-top:14px">${notifyLink('可視パスをLINEで通知する', 'btn btn-info btn-sm')}</div>
      </div>
      <div class="space-card glass">
        <div class="sc-head"><span class="sc-title">打ち上げた衛星を追う</span></div>
        <p class="ctx" style="margin-bottom:10px">打ち上げ後の衛星は、投入軌道によっては地上から追えます。ミッションごとの軌道情報は手配ページに表示します。</p>
        <div class="spec-row"><span class="k">追える条件</span><span class="v" style="font-size:13px">低軌道・薄明</span></div>
        <div class="spec-row"><span class="k">目安</span><span class="v" style="font-size:13px">分離後〜数日</span></div>
      </div>
      <div class="space-card glass">
        <div class="sc-head"><span class="sc-title">見え方のコツ</span></div>
        <ul class="tips-list">
          <li>日没後1〜2時間が狙い目</li>
          <li>点滅せず滑らかに動く</li>
          <li>街灯が少なく好条件</li>
        </ul>
      </div>
    </div>
  </section>`;
}

function topSteps(): string {
  const steps = [
    ['01', '打ち上げを選ぶ', '地図のピン、またはカレンダーから1件だけ選びます。'],
    ['02', '日程と地域が自動で入る', '前後泊を含む日付、射場からの距離順の宿、最寄り空港が揃います。'],
    ['03', '公式サイトで予約', '当サイトは予約を代行しません。各予約サイトへ移動して確定します。'],
  ];
  return `
  <section class="section">
    <h2 class="section-h">3ステップで手配が終わる</h2>
    <div class="steps-grid" style="margin-top:16px">
      ${steps
        .map(
          ([n, h, p]) => `<div class="step-card glass"><div class="step-num">${n}</div><h3>${h}</h3><p>${p}</p></div>`,
        )
        .join('')}
    </div>
  </section>`;
}

function topAbout(): string {
  return `
  <section class="section">
    <div class="about-grid">
      <div class="glass-sub">
        <h3>このサイトについて</h3>
        <p>日本のロケット打ち上げを現地で観覧するための、移動・宿泊の手配を1ページに集約する無料ツールです。対応する射場は種子島宇宙センター・内之浦宇宙空間観測所・スペースポート紀伊・北海道スペースポートの4か所です。</p>
        <p style="margin-top:8px"><a class="link-arrow" href="#/about">くわしく →</a></p>
      </div>
      <div class="glass-sub">
        <h3>広告・アフィリエイトの表示</h3>
        <p>楽天トラベル・じゃらん等の広告を利用しています。リンク経由の予約で手数料を受け取ることがありますが、利用者の支払額は増えません。表示順は射場からの距離と日付のみで決めています。</p>
      </div>
    </div>
  </section>`;
}

// ============================================================================
// LAUNCHES ページ（一覧）
// ============================================================================

function renderLaunches(): void {
  const root = app();
  const total = state.launches.length;
  root.innerHTML = `
    ${bannerHtml()}
    <section class="launches-head">
      <span class="cnt num">${total} EVENTS · 2026</span>
      <h1 class="page-h">打ち上げ一覧</h1>
      <p class="lede" style="margin-top:14px">日本の4射場の予定を日付順に。日時が確定している回だけ、宿・レンタカー・航空券の手配リンクが開きます。</p>
    </section>
    <div class="filters-card glass" id="filters">${filtersInner()}</div>
    <div id="launch-list-body">${launchesBody()}</div>
  `;
}

function filtersInner(): string {
  const sites: [string, string][] = [
    ['all', 'すべて'],
    ['tanegashima', '種子島'],
    ['uchinoura', '内之浦'],
    ['spaceport_kii', '紀伊'],
    ['hokkaido_spaceport', '北海道'],
  ];
  const siteChips = sites
    .map(
      ([id, lbl]) =>
        `<button class="chip ${state.filterSite === id ? 'is-on' : ''}" data-action="filter-site" data-site="${id}">${lbl}</button>`,
    )
    .join('');
  return `
    <span class="flabel">射場</span>
    <div class="fgroup">${siteChips}</div>
    <span class="fdivider"></span>
    <span class="flabel">確度</span>
    <div class="fgroup">
      <button class="chip ${state.filterConf === 'confirmed' ? 'is-on tone-ok' : ''}" data-action="filter-conf" data-conf="confirmed">確定のみ</button>
      <button class="chip ${state.filterConf === 'all' ? 'is-on' : ''}" data-action="filter-conf" data-conf="all">仮日程も含む</button>
    </div>
    <div class="sort">
      <label class="flabel" style="margin-right:6px">並び</label>
      <select data-action="sort" aria-label="並び順"><option value="date">日付順</option></select>
    </div>`;
}

function filteredLaunches(): LaunchDTO[] {
  return sortedLaunches().filter((l) => {
    if (state.filterSite !== 'all' && l.siteId !== state.filterSite) return false;
    if (state.filterConf === 'confirmed' && !precisionInfo(l.netPrecision).bookable) return false;
    return true;
  });
}

function launchesBody(): string {
  if (!state.loaded) return `<p class="loading">打ち上げ情報を読み込み中…</p>`;
  const list = filteredLaunches();
  if (!list.length) {
    return `<p class="empty-note">条件に合う打ち上げがありません。フィルタを「すべて」「仮日程も含む」に戻すと表示されます。</p>`;
  }
  // 月グルーピング（確定/仮は月キー、時期未定は末尾の「時期未定」群）
  const groups = new Map<string, LaunchDTO[]>();
  const tbd: LaunchDTO[] = [];
  for (const l of list) {
    const p = precisionInfo(l.netPrecision);
    const ymd = jstDate(l.net);
    // 年内/時期未定は net が仮値になりがち＝月グループに入れず「時期未定」に集約
    if (!ymd || p.tier === 'tbd' || p.tier === 'year') {
      tbd.push(l);
      continue;
    }
    const key = `${ymd.y}年${ymd.m}月`;
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(l);
  }
  let html = '';
  for (const [month, items] of groups) {
    html += `<div class="month-head">${E(month)}</div><div class="launch-rows">${items.map(launchRow).join('')}</div>`;
  }
  if (tbd.length) {
    html += `<div class="month-head">時期未定</div><div class="launch-rows">${tbd.map(launchRow).join('')}</div>`;
  }
  html += notifyPanel();
  html += `<p class="src" style="margin-top:20px">打ち上げ＝The Space Devs Launch Library 2 の取得値。並び順は日付のみで決めており、広告手数料では変更していません。</p>`;
  return html;
}

function launchRow(l: LaunchDTO): string {
  const p = precisionInfo(l.netPrecision);
  const site = l.siteId ? getSiteById(l.siteId) : undefined;
  const ymd = jstDate(l.net);
  const d = formatLaunchDate(l.net, l.netPrecision);
  const bookable = p.bookable;
  const dayCell = bookable && ymd
    ? `<span class="lr-day"><span class="d num">${ymd.d}</span><span class="w">${d.main.match(/\((.)\)/)?.[1] ?? ''}</span></span>`
    : `<span class="lr-day tentative"><span class="d">${E(tentativeDay(l))}</span></span>`;
  const pad = l.padName ? padCode(l.padName) : '';
  const extra = bookable && site ? `<span class="lr-extra">${E(site.nameJa.replace(/（.*/, ''))} ${pad ? '· ' + pad : ''}／${E(d.sub)}</span>` : '';
  const siteLine = !bookable
    ? `<span class="lr-site">${E(site?.nameJa.replace(/（.*/, '') || '—')}${pad ? ' ' + pad : ''}／日付は仮</span>`
    : '';
  const act = bookable
    ? `<button class="btn btn-ink btn-sm" data-action="select-launch" data-id="${E(l.id)}">この回で手配</button>`
    : notifyLink('LINEで通知を受け取る →', 'link-arrow');
  const cls = bookable ? 'glass confirmed' : p.tier === 'tbd' ? 'glass-faint tbd' : 'glass-sub tentative';
  return `
  <div class="lrow ${cls}">
    ${dayCell}
    <div class="lr-body">
      <div class="lr-titleline">${confBadge(l, true)}<span class="lr-veh">${E(vehicleLabel(l))}</span></div>
      ${siteLine}${extra}
    </div>
    <div class="lr-act">${act}</div>
  </div>`;
}

function tentativeDay(l: LaunchDTO): string {
  const p = precisionInfo(l.netPrecision);
  const ymd = jstDate(l.net);
  if (p.tier === 'tbd' || !ymd) return '未定';
  if (p.tier === 'week') return '中旬';
  if (p.tier === 'month') return '月内';
  if (p.tier === 'quarter') return `Q${l.netPrecision.toUpperCase()[1] ?? ''}`;
  if (p.tier === 'year') return '年内';
  return '未定';
}

function padCode(padName: string): string {
  const m = padName.match(/\b(LP-?\d|LC-?\d|M-\S+)\b/);
  return m && m[1] ? m[1] : '';
}

function notifyPanel(): string {
  return `
  <div class="notify-panel info-panel">
    <div>
      <h3>日程が決まったら通知します</h3>
      <p>仮日程の回は、正式発表と延期のタイミングで公式LINEからお知らせします。宿の予約は日程確定後が安全です。</p>
    </div>
    ${notifyLink('LINEで通知を登録する', 'btn btn-primary')}
  </div>`;
}

// ============================================================================
// ARRANGE ページ（手配）
// ============================================================================

// ---- 旅程条件（出発日・帰着日・出発地・人数）--------------------------------
//
// ★ここが手配ページの唯一の入力元。宿の空室検索も、レンタカー・航空券・体験の
//   外部リンクも、すべて currentTrip() が返す1つの値から作る。
//   利用者が同じ条件を予約サイト側でもう一度入力しなくて済むようにするのが目的。

/** 打ち上げ日から自動算出する既定の旅程（前日入り〜翌日帰り）。 */
function autoTripDates(l: LaunchDTO): { departIso: string; returnIso: string } {
  const stay = computeStay(l.net, l.netPrecision, state.pre, state.post);
  const today = todayIsoJst();
  if (!stay) return { departIso: today, returnIso: addDaysIso(today, 1) };
  return { departIso: stay.checkInIso, returnIso: stay.checkOutIso };
}

/** 利用者が日付を自分で入れ直しているか（＝「打ち上げに合わせる」で戻せる状態か）。 */
function isTripCustom(l: LaunchDTO): boolean {
  const ov = state.tripOverride;
  if (!ov || ov.launchId !== l.id) return false;
  const auto = autoTripDates(l);
  return ov.departIso !== auto.departIso || ov.returnIso !== auto.returnIso;
}

/**
 * いま有効な旅程条件。手動上書きが無ければ打ち上げ日から自動算出する。
 *
 * ★丸め窓に打ち上げ日を渡すのが要点（2026-08-04）。渡さないと、1年より先の回で
 *   自動算出した日付が「今日+365日」へ押し戻され、利用者に非がないのに
 *   「打ち上げ日が含まれていません」と表示されていた。
 */
function currentTrip(l: LaunchDTO): TripConditions {
  const ov = state.tripOverride;
  const base = ov && ov.launchId === l.id ? { departIso: ov.departIso, returnIso: ov.returnIso } : autoTripDates(l);
  const d = clampDates(base.departIso, base.returnIso, todayIsoJst(), 'depart', launchDayIsoOf(l));
  return { ...d, adults: state.adults, rooms: state.rooms };
}

/** 打ち上げ日（JSTのカレンダー日）。日付が未確定の回は null。 */
function launchDayIsoOf(l: LaunchDTO): string | null {
  if (!precisionInfo(l.netPrecision).bookable) return null;
  const ymd = jstDate(l.net);
  return ymd ? isoDate(ymd) : null;
}

function persistTripDates(launchId: string, departIso: string, returnIso: string): void {
  state.tripOverride = { launchId, departIso, returnIso };
  try {
    localStorage.setItem('rvp.trip.launch', launchId);
    localStorage.setItem('rvp.trip.depart', departIso);
    localStorage.setItem('rvp.trip.return', returnIso);
  } catch {
    /* プライベートブラウズ等で保存できなくても動作は続ける */
  }
}
function clearTripDates(): void {
  state.tripOverride = null;
  try {
    localStorage.removeItem('rvp.trip.launch');
    localStorage.removeItem('rvp.trip.depart');
    localStorage.removeItem('rvp.trip.return');
  } catch {
    /* noop */
  }
}

/** 出発地のセレクト群（都道府県＋県内に複数あるときだけ空港）。 */
function tripFromFields(): string {
  const { pref, air } = currentDeparture();
  return (
    `<label class="tf-k" for="dep-pref">出発地<span class="tf-hint">自分の街</span></label>` +
    `<div class="tf-v">${departurePrefSelect(pref.code)}${departureAirportSelect(pref, air.iata)}</div>` +
    // 推定であることを隠さない。当たっていれば触らずに済み、外れていれば直せる。
    (departureOrigin === 'geo'
      ? `<span class="tf-note">※現在地（接続元）から推定しました。違うときは選び直してください</span>`
      : '') +
    (pref.borrowed
      ? `<span class="tf-note">※${E(pref.nameJa)}に定期便の空港が無いため最寄りの主要空港</span>`
      : '')
  );
}

/** 条件のまとめ1行（区間・泊数・人数・打ち上げ日を含むか）。 */
function tripSummaryHtml(l: LaunchDTO, site: LaunchSite, trip: TripConditions): string {
  const { air } = currentDeparture();
  const dest = site.airports.find((a) => a.flightPanelDefault) ?? site.airports[0];
  const launchDayIso = launchDayIsoOf(l);
  const covered = !!launchDayIso && trip.departIso <= launchDayIso && launchDayIso <= trip.returnIso;
  return (
    `<b>${E(tripSummaryLabel(trip))}</b>` +
    `<span class="trip-route">${E(shortAirportName(air))}（${E(air.iata)}）→ ${E(dest?.nameJa.replace(/（.*/, '') ?? '')}（${E(dest?.iata ?? '')}）</span>` +
    (covered ? `<span class="trip-ok">打ち上げ日 ${E(labelJp(launchDayIso!))} を含む</span>` : '')
  );
}

/** 「その日程だと打ち上げが見られない/延期に耐えられない/まだ予約できない」の指摘。 */
function tripIssuesHtml(l: LaunchDTO, trip: TripConditions): string {
  const notice = bookingWindowNotice(trip.departIso, todayIsoJst());
  const issues = [...tripIssues(trip, launchDayIsoOf(l)), ...(notice ? [notice] : [])];
  const cls = { error: 'is-error', warn: 'is-warn', info: 'is-info' } as const;
  const icon = { error: '⚠️', warn: 'ℹ️', info: '🗓' } as const;
  return issues
    .map((i) => `<p class="trip-issue ${cls[i.level]}">${icon[i.level]} ${E(i.message)}</p>`)
    .join('');
}

function numOptions(from: number, to: number, selected: number, unit: string): string {
  let out = '';
  for (let n = from; n <= to; n++) {
    out += `<option value="${n}"${n === selected ? ' selected' : ''}>${n}${unit}</option>`;
  }
  return out;
}

/** 旅程条件の入力カード。 */
function tripCard(l: LaunchDTO, site: LaunchSite, trip: TripConditions): string {
  const dr = departRange(todayIsoJst(), launchDayIsoOf(l));
  const rr = returnRange(trip.departIso);
  return `
  <section class="trip-card glass" id="trip-conditions">
    <div class="trip-head">
      <h2 class="section-h">旅程の条件</h2>
      <p class="ctx">いつ自分の街を出て、いつ帰るか。ここで一度だけ入れれば、下の<b>宿・レンタカー・航空券・体験</b>のリンクは<b>この条件が入った状態</b>で開きます。予約サイト側で入れ直す必要はありません。</p>
    </div>
    <div class="trip-fields">
      <div class="tf tf-from" id="trip-from">${tripFromFields()}</div>
      <div class="tf">
        <label class="tf-k" for="trip-depart">出発日<span class="tf-hint">自分の街を出る日</span></label>
        <input class="tf-date" type="date" id="trip-depart" value="${E(trip.departIso)}" min="${E(dr.min)}" max="${E(dr.max)}" />
      </div>
      <span class="tf-arrow" aria-hidden="true">→</span>
      <div class="tf">
        <label class="tf-k" for="trip-return">帰着日<span class="tf-hint">自分の街に帰る日</span></label>
        <input class="tf-date" type="date" id="trip-return" value="${E(trip.returnIso)}" min="${E(rr.min)}" max="${E(rr.max)}" />
      </div>
      <div class="tf tf-num">
        <label class="tf-k" for="trip-adults">人数・部屋<span class="tf-hint">宿の検索に使います</span></label>
        <div class="tf-v">
          <select class="dep-sel" id="trip-adults" aria-label="大人の人数">${numOptions(MIN_ADULTS, MAX_ADULTS, trip.adults, '名')}</select>
          <select class="dep-sel" id="trip-rooms" aria-label="部屋数">${numOptions(MIN_ROOMS, MAX_ROOMS, trip.rooms, '室')}</select>
        </div>
      </div>
    </div>
    <div class="trip-foot">
      <p class="trip-sum" id="trip-sum">${tripSummaryHtml(l, site, trip)}</p>
      <button class="btn btn-glass btn-sm" data-action="trip-reset"${isTripCustom(l) ? '' : ' disabled'}>打ち上げに合わせる</button>
    </div>
    <div id="trip-issues">${tripIssuesHtml(l, trip)}</div>
  </section>`;
}

/**
 * 旅程カードの「変わった部分だけ」を書き換える。
 * ★カードごと再描画しないのは、入力中の要素がDOMごと消えるとフォーカスと
 *   モバイルの日付ピッカーが飛ぶため。値・範囲・注意書きだけを差し替える。
 */
function updateTripCardParts(l: LaunchDTO, site: LaunchSite, trip: TripConditions): void {
  const sum = document.getElementById('trip-sum');
  if (sum) sum.innerHTML = tripSummaryHtml(l, site, trip);
  const iss = document.getElementById('trip-issues');
  if (iss) iss.innerHTML = tripIssuesHtml(l, trip);

  const dep = document.getElementById('trip-depart') as HTMLInputElement | null;
  if (dep) {
    const dr = departRange(todayIsoJst(), launchDayIsoOf(l));
    dep.min = dr.min;
    dep.max = dr.max;
    if (dep.value !== trip.departIso) dep.value = trip.departIso;
  }
  const ret = document.getElementById('trip-return') as HTMLInputElement | null;
  if (ret) {
    const rr = returnRange(trip.departIso);
    ret.min = rr.min;
    ret.max = rr.max;
    if (ret.value !== trip.returnIso) ret.value = trip.returnIso;
  }
  const reset = document.querySelector<HTMLButtonElement>('[data-action="trip-reset"]');
  if (reset) reset.disabled = !isTripCustom(l);
}

/**
 * 旅程条件が変わったときに、それに依存する画面を作り直す。
 * 宿は日付・人数が変わったときだけ再取得する（出発地だけ変えて再検索が走るのは無駄）。
 */
function refreshTripDependent(opts: { reloadHotels: boolean }): void {
  const l = launchById(state.selectedLaunchId);
  const site = state.selectedSiteId ? getSiteById(state.selectedSiteId) : null;
  if (state.route.page !== 'arrange' || !l || !site) return;
  const trip = currentTrip(l);

  /*
   * 「予約受付前」と通常表示はセクション構成そのものが違う（宿・レンタカー・航空券が有るか無いか）。
   * 日付の変更でこの境界をまたいだときだけ、部分差し替えではなくページを作り直す。
   */
  if (isBeyondBookingWindow(trip.departIso, todayIsoJst()) !== arrangeBeyondWindow) {
    renderArrange();
    return;
  }
  updateTripCardParts(l, site, trip);

  const car = document.getElementById('arr-car');
  if (car) car.outerHTML = rentalcarSection(site, trip);
  const flight = document.getElementById('arr-flight');
  if (flight) flight.outerHTML = flightSection(site, trip);
  const sched = document.getElementById('arr-schedule');
  if (sched) sched.outerHTML = scheduleCard(site, l, trip);
  const stayHead = document.getElementById('arr-stay-sub');
  if (stayHead) stayHead.textContent = hotelSubLabel(site, trip);

  if (opts.reloadHotels) {
    const body = document.getElementById('hotel-body');
    if (body) {
      body.innerHTML = `<p class="loading">この条件で空室を検索中…</p>`;
      void loadHotels(site, trip);
    }
  }
}

function renderArrange(): void {
  const root = app();
  if (!state.loaded) {
    root.innerHTML = `<p class="loading">読み込み中…</p>`;
    return;
  }
  const l = launchById(state.selectedLaunchId) ?? firstBookable();
  if (!l || !l.siteId) {
    root.innerHTML = `
      ${bannerHtml()}
      <section class="launches-head"><h1 class="page-h">手配する打ち上げを選ぶ</h1>
      <p class="lede" style="margin-top:12px">まだ打ち上げが選ばれていません。一覧から日時が確定した回を選んでください。</p></section>
      <div style="margin-top:20px"><a class="btn btn-primary" href="#/launches">打ち上げ一覧へ</a></div>`;
    return;
  }
  state.selectedLaunchId = l.id;
  state.selectedSiteId = l.siteId;
  const site = getSiteById(l.siteId)!;
  const p = precisionInfo(l.netPrecision);
  const trip = currentTrip(l);
  const d = formatLaunchDate(l.net, l.netPrecision);
  const du = daysUntil(l.net);
  /*
   * ★予約サイトがまだ売っていない日程か（今日+365日より先）。
   *   この状態で /api/hotels を叩くと、サーバが日付を今日へ丸めて**別の日の空室**を返す。
   *   0件でも「満室」でもなく、単に受付前。予約導線を出す代わりに、そう書く。
   */
  const beyond = p.bookable && isBeyondBookingWindow(trip.departIso, todayIsoJst());
  arrangeBeyondWindow = beyond;

  root.innerHTML = `
    ${bannerHtml()}
    <p class="breadcrumb"><a href="#/">トップ</a> ／ <a href="#/launches">打ち上げ一覧</a> ／ ${E(vehicleLabel(l))}</p>
    <section class="arr-summary glass-hero">
      <div>
        <div class="sum-badges">${confBadge(l)}${l.padName ? `<span class="badge pad-code">${E(padCode(l.padName) || l.padName)}</span>` : ''}<span class="badge tone-info">${E(statusInfo(l.statusAbbrev, l.status).label)}</span></div>
        <h1 class="arr-title">${E(vehicleLabel(l))}</h1>
        <div class="sum-facts">
          <div class="sum-fact"><div class="k">打ち上げ日時（JST）</div><div class="v">${E(d.main)}<br /><span class="u">${E(d.sub)}</span></div></div>
          <div class="sum-fact"><div class="k">射場</div><div class="v ink">${E(site.nameJa.replace(/（.*/, ''))}</div></div>
          <div class="sum-fact"><div class="k">残り</div><div class="v">${du != null && du >= 0 ? du : '—'}<span class="u">日</span></div></div>
        </div>
      </div>
      <div class="sum-cta">
        ${p.bookable && beyond
          ? `${notifyLink('予約開始のころにLINEで知らせる', 'btn btn-primary btn-lg')}
             <a class="btn btn-glass" href="#/guide?site=${E(site.id)}">観覧スポットを見る</a>
             <p class="hint">この日程はまだ宿・航空券の発売前です（日程だけ先に決められます）</p>`
          : p.bookable
          ? `<button class="btn btn-primary btn-lg" data-action="scroll" data-target="arr-stay">この日程で宿を見る</button>
             <a class="btn btn-glass" href="#/guide?site=${E(site.id)}">観覧スポットを見る</a>
             <p class="hint">延期時はこのページの日程が自動で更新されます</p>`
          : `${notifyLink('日程確定をLINEで通知する', 'btn btn-primary btn-lg')}
             <a class="btn btn-glass" href="#/guide?site=${E(site.id)}">観覧スポットを見る</a>
             <p class="hint">この回は日付が仮です。確定してから宿を押さえるのが安全です。</p>`}
      </div>
    </section>

    ${p.bookable ? tripCard(l, site, trip) : ''}

    ${p.bookable && !beyond
      ? `<div class="cat-chips">
           <button class="chip is-on" data-action="scroll" data-target="arr-stay">宿</button>
           <button class="chip" data-action="scroll" data-target="arr-car">レンタカー</button>
           <button class="chip" data-action="scroll" data-target="arr-flight">航空券</button>
           <a class="chip" href="#/guide?site=${E(site.id)}">観覧スポット</a>
         </div>`
      : ''}

    <div class="arr-grid">
      <div class="arr-main">
        ${!p.bookable ? tentativeMainPanel() : beyond ? notYetBookablePanel(site, trip) : hotelSection(site, trip)}
        ${p.bookable && !beyond ? rentalcarSection(site, trip) : ''}
        ${p.bookable && !beyond ? flightSection(site, trip) : ''}
      </div>
      <aside class="arr-side">
        ${p.bookable ? scheduleCard(site, l, trip) : ''}
        ${missionCard(l)}
        ${postponeCard(site)}
        ${siteGuideMiniCard(site, l)}
      </aside>
    </div>
  `;
  if (p.bookable && !beyond) void loadHotels(site, trip);
}

/**
 * 「日程は正しいが、まだ売っていない」ときの本文。
 *
 * ★ここで予約リンクを並べないのが要点。1年以上先の日程で予約サイトへ送ると、
 *   条件は無視されるか0件になり、利用者には「このサイトは壊れている」ようにしか見えない。
 * 期間の根拠: 宿検索の窓=今日+365日（src/lib/hotel-params.ts）／
 *             楽天レンタカーの在庫=約8か月先まで（2026-08-01実測・src/lib/deeplinks.ts）。
 */
function notYetBookablePanel(site: LaunchSite, trip: TripConditions): string {
  const days = diffDays(todayIsoJst(), trip.departIso);
  return `
  <section class="glass" style="padding:24px 26px" id="arr-stay">
    <h2 class="section-h">この日程はまだ予約受付前です</h2>
    <p class="lede" style="margin-top:10px">出発日は <b>${E(labelJp(trip.departIso))}</b>（約${Math.floor(days / 30)}か月先）です。国内の宿・航空券はおおむね出発の1年前ごろから、レンタカーは約8か月前から販売が始まります。いま予約サイトへ送っても空室・空席は出ないため、この回では予約リンクを出していません。</p>
    <p class="ctx" style="margin-top:10px">上の「旅程の条件」は<b>打ち上げ日に合わせて入力済み</b>です。人数や日数だけ先に決めておけば、受付が始まってからそのまま使えます。</p>
    <div style="margin-top:16px">${notifyLink('予約開始のころにLINEで知らせる', 'btn btn-primary')}</div>
    <p class="src" style="margin-top:14px">先に下調べをするなら<a href="#/guide?site=${E(site.id)}">${E(site.nameJa.replace(/（.*/, ''))}の射場ガイド</a>へ。${E(GLOBAL_CAVEAT)}</p>
  </section>`;
}

function tentativeMainPanel(): string {
  return `
  <section class="glass" style="padding:24px 26px" id="arr-stay">
    <h2 class="section-h">この回は日付が仮です</h2>
    <p class="lede" style="margin-top:10px">正式な打ち上げ日が発表される前に宿や航空券を確定すると、延期で損をする恐れがあります。そのため、この回では予約リンクを出していません。日程が決まりしだいお知らせします。</p>
    <div style="margin-top:16px">${notifyLink('日程確定をLINEで通知する', 'btn btn-primary')}</div>
    <p class="src" style="margin-top:14px">${E(GLOBAL_CAVEAT)}</p>
  </section>`;
}

function scheduleCard(site: LaunchSite, l: LaunchDTO, trip: TripConditions): string {
  const anchor = jstDate(l.net)!;
  const primary = site.airports.find((a) => a.flightPanelDefault) ?? site.airports[0];
  const nights = nightsOf(trip);
  const rows: [string, string, string][] = [
    [
      labelJp(trip.departIso),
      '出発・現地入り',
      `${shortAirportName(currentDeparture().air)} → ${primary?.nameJa.replace(/（.*/, '') || site.nameJa} 着・レンタカー受取`,
    ],
    [
      `${anchor.m}/${anchor.d}`,
      `打ち上げ当日 ${formatLaunchDate(l.net, l.netPrecision).sub.replace(' JST', '')}`,
      '観覧場所へは早めに到着（射場周辺は当日混雑）',
    ],
    [
      labelJp(trip.returnIso),
      '帰着（レンタカー返却・復路）',
      nights >= 3 ? '予備日を含む日程です' : '延期に備えて1泊多めが安全',
    ],
  ];
  return `
  <div class="side-card glass" id="arr-schedule">
    <div class="side-h">この旅程</div>
    <div class="timeline">
      ${rows
        .map(
          ([dt, t, s]) => `<div class="tl-row"><span class="tl-date">${E(dt)}</span><div class="tl-body"><b>${E(t)}</b><span>${E(s)}</span></div></div>`,
        )
        .join('')}
    </div>
    <p class="side-note">日付は上の「旅程の条件」で変えられます（宿・レンタカー・航空券のリンクにも同時に反映されます）。</p>
  </div>`;
}

function missionCard(l: LaunchDTO): string {
  const orbitLabel = l.orbit || l.missionType || '軌道';
  const specs: [string, string][] = [];
  if (missionOf(l)) specs.push(['ペイロード', missionOf(l)]);
  if (l.orbit) specs.push(['投入軌道', l.orbit]);
  if (l.missionType) specs.push(['ミッション種別', l.missionType]);
  if (l.provider) specs.push(['打ち上げ提供', l.provider]);
  return `
  <div class="side-card glass">
    <div class="side-h">ミッションと軌道<span class="sc-right" style="float:right;font-weight:400">${E(rocketOf(l))}</span></div>
    ${orbitDiagramSvg(E(orbitLabel))}
    <div class="side-spec">
      ${specs.length
        ? specs.map(([k, v]) => `<div class="spec-row"><span class="k">${E(k)}</span><span class="v" style="font-size:12.5px">${E(v)}</span></div>`).join('')
        : '<p class="ctx">軌道の詳細は打ち上げ提供者の発表で確認してください。</p>'}
    </div>
    <p class="side-note">高度・傾斜角・分離時刻などの詳細値はミッションごとに異なります。正確な数値は提供者の公式発表をご確認ください。</p>
  </div>`;
}

function postponeCard(_site: LaunchSite): string {
  return `
  <div class="side-card info-panel">
    <div class="side-h">延期に備える</div>
    <p class="ctx">打ち上げは天候や機体都合で延期が常態です。宿は無料キャンセル可のプラン、航空券は変更可の運賃を選ぶと、日程がずれても損失が出にくくなります。</p>
    <div style="margin-top:12px">${notifyLink('LINEで延期通知を受け取る', 'btn btn-info btn-sm')}</div>
  </div>`;
}

function siteGuideMiniCard(site: LaunchSite, _l: LaunchDTO): string {
  const g = getGuideById(site.id);
  const spotCount = g?.spots.length ?? 0;
  const primary = site.airports.find((a) => a.role === 'primary') ?? site.airports[0];
  return `
  <div class="side-card glass-sub">
    <div class="side-h">${E(site.nameJa.replace(/（.*/, ''))}の観覧</div>
    <div class="spec-row"><span class="k">定番の観覧場所</span><span class="v" style="font-size:13px">${spotCount}か所</span></div>
    <div class="spec-row"><span class="k">最寄り空港</span><span class="v" style="font-size:13px">${E(primary?.iata || '—')}</span></div>
    <div class="spec-row"><span class="k">当日の交通規制</span><span class="v" style="font-size:13px">あり</span></div>
    <p style="margin-top:12px"><a class="link-arrow" href="#/guide?site=${E(site.id)}">射場ガイドを読む →</a></p>
  </div>`;
}

// ---- ホテル（楽天空室・非同期） -------------------------------------------

const DEMO = new URLSearchParams(location.search).has('demo');
// ?demo=1 用のモック。imageUrl は null（実在しない施設の写真は作れない＝プレースホルダ表示の確認用）。
const MOCK_HOTELS: HotelsResponse['hotels'] = [
  { hotelNo: 1, name: '南種子 ホテル A', distanceKm: 6.2, minCharge: 12800, review: 4.31, url: '#', lat: 30.4306, lon: 130.985, address: '鹿児島県熊毛郡南種子町', imageUrl: null },
  { hotelNo: 2, name: '南種子 民宿 B', distanceKm: 8.9, minCharge: 9400, review: 4.05, url: '#', lat: 30.4525, lon: 130.9705, address: '南種子町島間', imageUrl: null },
  { hotelNo: 3, name: '西之表 ビジネスホテル C', distanceKm: 24.1, minCharge: 7600, review: null, url: '#', lat: 30.7325, lon: 130.9915, address: '西之表市', imageUrl: null },
  { hotelNo: 4, name: 'ロケットビュー中種子', distanceKm: 14.9, minCharge: 11000, review: 4.6, url: '#', lat: 30.5325, lon: 130.9705, address: '中種子町野間', imageUrl: null },
];

/** 「9/10(木)〜9/13(日)・大人2名1室・種子島宇宙センターから距離が近い順」 */
function hotelSubLabel(site: LaunchSite, trip: TripConditions): string {
  const rooms = trip.rooms > 1 ? `${trip.rooms}室` : '1室';
  return `${labelJp(trip.departIso)}〜${labelJp(trip.returnIso)}・大人${trip.adults}名${rooms}・${site.nameJa.replace(/（.*/, '')}から距離が近い順`;
}

function hotelSection(site: LaunchSite, trip: TripConditions): string {
  return `
  <section id="arr-stay">
    <div class="sec-head"><h2 class="section-h">周辺の宿</h2><span class="sub" id="arr-stay-sub">${E(hotelSubLabel(site, trip))}</span></div>
    <div id="hotel-body"><p class="loading">空室を検索中…</p></div>
  </section>`;
}

async function loadHotels(site: LaunchSite, trip: TripConditions): Promise<void> {
  const body = document.getElementById('hotel-body');
  if (!body) return;
  /*
   * 受付窓の外は検索しない（最後の砦）。/api/hotels は窓外の日付を今日へ丸めるので、
   * 投げてしまうと**利用者が指定していない日の空室**を、その日程の結果として並べることになる。
   */
  if (isBeyondBookingWindow(trip.departIso, todayIsoJst())) {
    body.innerHTML = `<p class="scrub">この日程はまだ予約受付前です（宿の空室は出発の約1年前から）。</p>`;
    return;
  }
  const key = `${site.id}:${trip.departIso}:${trip.returnIso}:${state.radiusKm}:${trip.adults}:${trip.rooms}`;
  const reqId = ++hotelReqId;

  if (DEMO) {
    renderHotelBody(body, mockResp(site, trip), site, trip);
    return;
  }
  const cached = hotelCache.get(key);
  if (cached) {
    renderHotelBody(body, cached, site, trip);
    return;
  }
  try {
    const q = new URLSearchParams({
      site: site.id,
      checkin: trip.departIso,
      checkout: trip.returnIso,
      radius: String(state.radiusKm),
      adults: String(trip.adults),
      rooms: String(trip.rooms),
    });
    const res = await fetch(`/api/hotels?${q.toString()}`);
    const data = (await res.json()) as HotelsResponse;
    hotelCache.set(key, data);
    if (reqId !== hotelReqId) return;
    const b2 = document.getElementById('hotel-body');
    if (b2) renderHotelBody(b2, data, site, trip);
  } catch {
    if (reqId !== hotelReqId) return;
    const b2 = document.getElementById('hotel-body');
    if (b2) b2.innerHTML = `<p class="scrub">ホテル情報の取得に失敗しました。時間をおいて再度お試しください。</p>` + hotelGoogleSection(site, trip);
  }
}

function mockResp(site: LaunchSite, trip: TripConditions): HotelsResponse {
  return {
    hotels: MOCK_HOTELS.filter((h) => h.distanceKm <= state.radiusKm).sort((a, b) => a.distanceKm - b.distanceKm),
    siteId: site.id, checkinDate: trip.departIso, checkoutDate: trip.returnIso, radiusKm: state.radiusKm,
    adults: trip.adults, rooms: trip.rooms,
    method: 'area', coarse: false, droppedTiles: 0, droppedPages: 0, requests: 5, returned: MOCK_HOTELS.length, unparsed: 0, source: 'live', configured: true,
  };
}

function renderHotelBody(body: HTMLElement, data: HotelsResponse, site: LaunchSite, trip: TripConditions): void {
  const google = hotelGoogleSection(site, trip);
  if (!data.configured) {
    body.innerHTML =
      `<p class="placeholder">楽天トラベルAPIキーが未設定です。設定すると、ここに半径${state.radiusKm}km・<b>距離が近い順</b>の空室（施設名／直線距離／最安／評点／予約）が表示されます。今はGoogleで周辺のホテルを確認できます。</p>` +
      radiusButtons() + google;
    return;
  }
  if (data.hotels.length === 0) {
    body.innerHTML =
      `<p class="scrub">🈵 ${E(data.note || `半径${state.radiusKm}kmに空室が見つかりませんでした（満室の可能性）。`)}</p>` +
      radiusButtons() + google;
    return;
  }
  const rows = data.hotels.map(hotelCard).join('');
  /*
   * ★注記は data.note があれば必ず出す。
   *   以前は `data.coarse && data.note` を条件にしていたが、coarse は「区分コード未確定」の意味で、
   *   4射場すべてコードが確定した今は**常に false**。つまり
   *   「宿が1件でも表示されると note が絶対に出ない」状態だった（2026-07-31発覚）。
   *   消えていたのは、まさに不具合を知らせるための注記:
   *     ・取得N件のうちM件を読み取れなかった（＝表示の不具合。今回の事故の再発検知そのもの）
   *     ・楽天のレート制限/HTTPエラーで部分的な結果しか無い
   *   **部分的な結果を、完全な結果のような顔で出す**のが一番まずい。健全なときは note 自体が
   *   付かないので、条件を外しても通常表示にノイズは増えない（本番実測で note:(なし) を確認済み）。
   */
  const coarse = data.note ? `<p class="ctx small-note">${E(data.note)}</p>` : '';
  body.innerHTML =
    `<div style="display:flex;flex-direction:column;gap:12px">${rows}</div>` +
    coarse + radiusButtons() +
    `<div class="info-panel" style="padding:14px 16px;margin-top:6px"><p class="list-note">${E(`${labelJp(data.checkinDate || trip.departIso)}〜${labelJp(data.checkoutDate || trip.returnIso)}・大人${data.adults ?? trip.adults}名${(data.rooms ?? trip.rooms) > 1 ? `${data.rooms ?? trip.rooms}室` : '1室'}`)}で空室のある宿だけを表示しています。施設名・料金・空室は楽天トラベルの取得値。表示順は<b>射場からの距離（直線）</b>で、広告手数料では並べ替えていません。直線距離は道路の所要時間と異なります（離島・山道で近くても時間を要することあり）。${data.source === 'kv' ? '（キャッシュ表示）' : ''}</p></div>` +
    adNote('宿泊予約') + google;
}

function hotelCard(h: HotelsResponse['hotels'][number]): string {
  const price = h.minCharge != null ? `¥${h.minCharge.toLocaleString('ja-JP')}` : '—';
  const review = h.review != null ? `★${h.review.toFixed(2)}` : '';
  const book =
    h.url && h.url !== '#'
      ? `<a class="btn btn-ink btn-sm" href="${E(h.url)}" target="_blank" rel="noopener noreferrer sponsored nofollow">空室を見る</a>`
      : `<span class="btn btn-glass btn-sm" aria-disabled="true">空室を見る</span>`;
  // 施設写真は楽天トラベルが配信する「その施設の実写」。無い施設はプレースホルダのまま
  // （別の施設の写真や無関係なイメージ写真は入れない）。
  const thumb = h.imageUrl
    ? `<img class="thumb" src="${E(h.imageUrl)}" alt="" width="80" height="64" loading="lazy" decoding="async" referrerpolicy="no-referrer" />`
    : `<div class="thumb" aria-hidden="true"></div>`;
  return `
  <div class="hotel-card glass-sub">
    ${thumb}
    <div>
      <div class="h-name">${E(h.name)}</div>
      <div class="h-meta">射場から ${h.distanceKm.toFixed(1)}km（直線）${h.address ? '・' + E(h.address) : ''}${review ? '・' + review : ''}</div>
    </div>
    <div class="h-price"><span class="p num">${price}<span style="font-size:13px">〜</span></span><span class="u">1泊1名・税込</span>${book}</div>
  </div>`;
}

function radiusButtons(): string {
  const opts = [20, 40, 60];
  return (
    `<div class="radius-row"><span class="rlabel">検索半径</span>` +
    opts.map((r) => `<button class="rbtn ${r === state.radiusKm ? 'is-on' : ''}" data-action="set-radius" data-r="${r}" aria-pressed="${r === state.radiusKm}">${r}km</button>`).join('') +
    /*
     * ★「屋久島・鹿児島市内など」と書いていたが、**そこは一度も検索していない**（2026-07-31是正）。
     *   上流に投げているのは射場のある区分だけ（種子島なら smallClassCode='ritou'）で、
     *   屋久島は別区分 'yakushima'、鹿児島市は 'kagoshima'。半径を60kmにしても対象に入らない
     *   （鹿児島市は138.7kmでそもそも圏外）。**探していない場所を候補として案内しない。**
     *   半径を広げて実際に増えるのは「同じ区分の中の遠い宿」（種子島なら西之表 36〜43km）。
     */
    `<span class="rhint">満室なら広げる（同じ地域の遠い宿まで表示）</span></div>`
  );
}

function hotelGoogleSection(site: LaunchSite, trip: TripConditions): string {
  return `<h4 class="sub-h">ほかのサイトでも周辺の宿を見る</h4>` + deepLinkCards(otherHotelLinks(site, trip));
}

function rentalcarSection(site: LaunchSite, trip: TripConditions): string {
  const primary = site.airports.find((a) => a.role === 'primary') ?? site.airports[0];
  const pickup = primary ? `${primary.iata}（${primary.nameJa.replace(/（.*/, '')}）受取` : '空港受取';
  const links = rentacarLinks(site, trip);
  return `
  <section id="arr-car">
    <div class="arr-cathead"><h3>レンタカー</h3><span class="sub">${E(pickup)}・${E(labelJp(trip.departIso))}貸出〜${E(labelJp(trip.returnIso))}返却</span></div>
    <p class="scrub" style="margin-bottom:10px">⚠️ 打ち上げは延期が常態です。返却日は打ち上げ翌日以降に。延期に備え1日後ろ倒しを推奨。</p>
    ${deepLinkCards(links) + adNote('レンタカー')}
    <p class="src">受取拠点はレンタカーの検索画面で上記空港付近を選択してください（拠点コードはサービス固有のため自動指定していないものがあります）。</p>
  </section>`;
}

/** 現在の出発地（県 + 空港）。保存値が壊れていても既定に落ちる */
function currentDeparture(): { pref: DeparturePrefecture; air: DepartureAirport } {
  const pref = findPrefByCode(state.departurePref) ?? findPrefByCode(DEFAULT_PREF_CODE)!;
  return { pref, air: resolveAirport(pref, state.departureIata) };
}

/**
 * 出発地を接続元（IP）から推定して初期値に入れる。
 *
 * ★ブラウザの位置情報APIは使わない。許可ダイアログを出さずに済ませるためで、
 *   そもそも public/_headers の `Permissions-Policy: geolocation=()` で禁じている。
 *   推定は /api/geo（Cloudflare の request.cf を都道府県に写すだけ）。
 *
 * 規律:
 *  - 利用者が自分で選んだ値（departureOrigin === 'chosen'）は**絶対に上書きしない**。
 *  - 推定値は localStorage に保存しない。保存すると「選んだ」と区別が付かなくなり、
 *    次からは推定が外れても直しに来た人の選択に見えてしまう。
 *  - 失敗しても何も起きない（既定の東京のまま）。推定は無くても手配は成立する。
 */
async function detectDeparture(): Promise<void> {
  // 関数越しに読む＝await をまたいでも「その時点の値」を見る（変数を直接見ると型の絞り込みで固定される）
  const wasChosen = () => departureOrigin === 'chosen';
  if (wasChosen()) return;
  try {
    const res = await fetch('/api/geo');
    if (!res.ok) return;
    const data = (await res.json()) as { pref?: string | null };
    const code = data?.pref ?? null;
    // 応答が来るまでの間に利用者が選び直していたら、その選択を尊重して何もしない
    if (wasChosen() || !isKnownDeparturePref(code)) return;
    const pref = findPrefByCode(code)!;
    departureOrigin = 'geo';
    state.departurePref = pref.code;
    state.departureIata = resolveAirport(pref, null).iata;
    // 既に手配ページが描画済みなら、出発地ブロックと出発地に依存する表示だけ差し替える
    const from = document.getElementById('trip-from');
    if (from) {
      from.innerHTML = tripFromFields();
      refreshTripDependent({ reloadHotels: false }); // 宿は射場周辺なので出発地に依存しない
    }
  } catch {
    /* 推定できなくても既定のまま動く */
  }
}

/** 都道府県セレクト（地方ごとに optgroup） */
function departurePrefSelect(selected: string): string {
  const groups = DEPARTURE_REGIONS.map((region) => {
    const opts = DEPARTURE_PREFECTURES.filter((p) => p.region === region)
      .map(
        (p) =>
          `<option value="${E(p.code)}"${p.code === selected ? ' selected' : ''}>${E(p.nameJa)}</option>`,
      )
      .join('');
    return `<optgroup label="${E(region)}">${opts}</optgroup>`;
  }).join('');
  return `<select id="dep-pref" class="dep-sel" aria-label="出発地の都道府県">${groups}</select>`;
}

/** 県内に選択肢が複数あるときだけ出す空港セレクト（旅程条件カードの中に置く） */
function departureAirportSelect(pref: DeparturePrefecture, selectedIata: string): string {
  if (pref.airports.length < 2) return '';
  const opts = pref.airports
    .map(
      (a) =>
        `<option value="${E(a.iata)}"${a.iata === selectedIata ? ' selected' : ''}>${E(a.nameJa)}（${E(a.iata)}）</option>`,
    )
    .join('');
  return `<select id="dep-air" class="dep-sel" aria-label="出発空港">${opts}</select>`;
}

function flightSection(site: LaunchSite, trip: TripConditions): string {
  const dest = site.airports.find((a) => a.flightPanelDefault) ?? site.airports[0];
  const { pref, air } = currentDeparture();
  const links = dest ? flightLinks(air.iata, dest, trip, site) : [];
  return `
  <section id="arr-flight">
    <div class="arr-cathead"><h3>航空券</h3><span class="sub">${E(shortAirportName(air))} → ${E(dest?.nameJa.replace(/（.*/, '') || '')}・往復</span></div>
    <p class="ctx" style="margin-bottom:10px">区間：<b>${E(departureLabel(pref, air))}</b> → <b>${E(dest?.nameJa.replace(/（.*/, '') || '—')}（${E(dest?.iata || '—')}）</b>${
      pref.borrowed ? `<span class="tent">※${E(pref.nameJa)}に定期便の空港が無いため最寄りの主要空港</span>` : ''
    }<br />日付：往路 <b>${E(labelJp(trip.departIso))}</b> / 復路 <b>${E(labelJp(trip.returnIso))}</b>・大人${trip.adults}名（<button class="linkish" data-action="scroll" data-target="trip-conditions">旅程の条件を変える</button>）</p>
    ${dest ? deepLinkCards(links) + adNote('航空券') : `<p class="ctx">打ち上げを選ぶと、その日程でリンクを用意します。</p>`}
    <p class="src">選んだ出発地はブラウザに保存されます。${site.isIsland ? '※種子島は最寄空港まで空路乗継/フェリーが別途必要（射場ガイド参照）。' : ''}</p>
  </section>`;
}

// ============================================================================
// GUIDE ページ（射場ガイド）
// ============================================================================

function renderGuide(): void {
  const root = app();
  const siteId = state.route.query.get('site') || state.selectedSiteId || LAUNCH_SITES[0]!.id;
  const site = getSiteById(siteId) ?? LAUNCH_SITES[0]!;
  const g = getGuideById(site.id);
  const idx = LAUNCH_SITES.findIndex((s) => s.id === site.id);
  const primary = site.airports.find((a) => a.role === 'primary') ?? site.airports[0];
  const nl = state.loaded ? nextLaunchForSite(site.id) : null;
  // 体験リンクにも旅程の日付を載せる。日付が確定した回が無ければ日付なし（推測で埋めない）。
  // 受付窓より先の日程も日付なしで出す（体験予約サイトも1年以上先は受け付けていないため、
  // 日付を載せると条件が黙って無視されるか0件になる）。
  const nlTrip = nl && precisionInfo(nl.netPrecision).bookable ? currentTrip(nl) : null;
  const guideTrip = nlTrip && !isBeyondBookingWindow(nlTrip.departIso, todayIsoJst()) ? nlTrip : null;

  // 観覧スポット個別の実写は自由ライセンスで存在しない。ダミー写真で埋めると
  // 「その場所の写真」に見えてしまうため、写真枠は置かず情報だけを出す（src/data/photos.ts の方針）。
  const spots = (g?.spots ?? [])
    .map(
      (sp) => `
    <div class="spot-card glass-sub">
      <div>
        <div class="sp-name">${E(sp.name)}${sp.badge ? `<span class="pill">${E(sp.badge)}</span>` : ''}</div>
        <div class="sp-memo">${E(sp.memo)}</div>
      </div>
    </div>`,
    )
    .join('');
  const sitePhoto = getSitePhoto(site.id);

  root.innerHTML = `
    <p class="breadcrumb"><a href="#/">トップ</a> ／ 射場ガイド ／ ${E(site.nameJa.replace(/（.*/, ''))}</p>
    <section class="guide-head">
      <div>
        <p class="eyebrow-lg">VIEWING GUIDE · 0${idx + 1} / 04</p>
        <h1 class="page-h">${E(site.nameJa.replace(/（.*/, ''))}</h1>
        <p class="lede" style="margin-top:14px">${E(g?.lead || site.viewingNotes)}</p>
        ${sitePhoto ? photoFigure(sitePhoto, 'photo-guide') : ''}
      </div>
      <div class="guide-summary">
        <div class="guide-sum-card glass-sub"><div class="k">最寄り空港</div><div class="v" style="font-size:15px">${E(primary?.nameJa.replace(/（.*/, '') || '—')}</div><div class="sub">射場まで ${primary ? Math.round(primary.straightLineKm) : '—'}km（直線）</div></div>
        <div class="guide-sum-card glass-sub"><div class="k">宿の目安</div><div class="v" style="font-size:15px">${E(g?.lodgingArea || '射場周辺')}</div><div class="sub">に点在</div></div>
        <div class="guide-sum-card glass-sub"><div class="k">観覧場所</div><div class="v">${g?.spots.length ?? '—'}か所</div><div class="sub">${E(g?.viewingAccessHint || '車必須')}</div></div>
      </div>
    </section>

    <div class="guide-grid">
      <div class="guide-main">
        <h2 class="section-h" style="margin-bottom:4px">定番の観覧スポット</h2>
        ${spots || '<p class="ctx">観覧スポットは打ち上げごとに公式が告知します。</p>'}
        <div class="checklist glass" style="margin-top:10px">
          <h3>当日の持ち物・注意</h3>
          <ul>${VIEWING_CHECKLIST.map((c) => `<li>${E(c)}</li>`).join('')}</ul>
        </div>
        <div style="margin-top:22px">${activitySection(site, guideTrip)}</div>
      </div>
      <aside class="guide-side">
        <div class="access-card glass">
          <div class="side-h" style="font-size:15px;font-weight:700;margin-bottom:4px">アクセス</div>
          ${accessBlocks(site)}
        </div>
        ${nl ? nextLaunchPanel(site, nl) : ''}
        <div class="other-sites glass-sub">
          <div class="side-h" style="font-size:14px;font-weight:700;margin-bottom:4px">ほかの射場</div>
          ${LAUNCH_SITES.filter((s) => s.id !== site.id)
            .map((s) => `<button class="os-row" data-action="guide-site" data-site="${E(s.id)}">${E(s.nameJa.replace(/（.*/, ''))}<span class="arr">→</span></button>`)
            .join('')}
        </div>
      </aside>
    </div>
    <p class="src" style="margin-top:24px">観覧場所・駐車台数・交通規制は打ち上げごとに変わります。必ず公式発表を確認してください。／写真は射場周辺の実写です（撮影時期はキャプション参照）。観覧スポットごとの写真は、自由に使える実写が無いため掲載していません。</p>
  `;
}

function activitySection(site: LaunchSite, trip: TripConditions | null): string {
  return (
    `<h2 class="section-h" style="margin-bottom:4px">周辺の見どころ・体験</h2>` +
    `<p class="ctx" style="margin-bottom:10px">打ち上げは延期が多く、待ち時間が空きます。前後に組める体験を探せます。${
      trip ? `（${E(labelJp(trip.departIso))}〜${E(labelJp(trip.returnIso))}の滞在中）` : ''
    }</p>` +
    deepLinkCards(activityLinks(site, trip)) +
    adNote('体験・アクティビティ')
  );
}

function accessBlocks(site: LaunchSite): string {
  const blocks: [string, string][] = [];
  const primary = site.airports.find((a) => a.role === 'primary') ?? site.airports[0];
  if (primary) blocks.push(['AIR', `${primary.iata} ${primary.nameJa.replace(/（.*/, '')}（直線 約${Math.round(primary.straightLineKm)}km）。${primary.access}`]);
  const hub = site.airports.find((a) => a.role === 'hub');
  if (hub) blocks.push([site.isIsland ? 'SEA / 乗継' : 'HUB', hub.access]);
  const alt = site.airports.find((a) => a.role === 'alt');
  if (alt) blocks.push(['ALT', `${alt.iata} ${alt.nameJa.replace(/（.*/, '')}：${alt.access}`]);
  const sk = site.nearestShinkansen;
  blocks.push(['RAIL', `${sk.stationJa}駅（直線 約${Math.round(sk.straightLineKm)}km・${sk.isPractical ? '一応可' : '非現実的'}）。${sk.realisticTravel}`]);
  return blocks.map(([lbl, txt]) => `<div class="ab"><div class="lbl">${E(lbl)}</div><p>${E(txt)}</p></div>`).join('');
}

function nextLaunchPanel(site: LaunchSite, l: LaunchDTO): string {
  const d = formatLaunchDate(l.net, l.netPrecision);
  const p = precisionInfo(l.netPrecision);
  return `
  <div class="next-launch-panel info-panel">
    <div class="k">この射場の次の打ち上げ</div>
    <div class="veh">${E(vehicleLabel(l))}</div>
    <div class="dt">${E(d.main)}<br /><span style="font-size:13px;color:var(--muted);font-family:var(--f-ui);font-weight:500">${E(d.sub)}</span></div>
    ${p.bookable
      ? `<button class="btn btn-primary btn-block" data-action="select-launch" data-id="${E(l.id)}">この回で手配する</button>`
      : notifyLink('日程確定をLINEで通知する', 'btn btn-primary btn-block')}
  </div>`;
}

// ============================================================================
// ABOUT ページ
// ============================================================================

function renderAbout(): void {
  const root = app();
  root.innerHTML = `
    <div class="about-page">
    <section class="launches-head"><p class="eyebrow-lg">ABOUT THIS SITE</p><h1 class="page-h">観覧の手配を、1ページに集約する無料ツールです</h1></section>
    <section class="section"><div class="glass">
      <h3>目的</h3>
      <p>日本のロケット打ち上げを現地で観覧するとき、宿・レンタカー・航空券を「同じ日付・同じ地域」で一度に手配できるようにするサイトです。打ち上げを1件選ぶと、その日程と射場に合わせた手配情報がまとまって並びます。予約は各公式サイトで行います（当サイトは予約を代行しません）。</p>
    </div></section>
    <section class="section"><div class="glass">
      <h3>対応する射場</h3>
      <ul class="bullets">
        ${LAUNCH_SITES.map((s) => `<li>${E(s.nameJa)}（${E(s.operator)}）</li>`).join('')}
      </ul>
    </div></section>
    <section class="section"><div class="glass">
      <h3>表示順のルール</h3>
      <p>ホテルは<b>射場からの距離（直線）</b>、打ち上げは<b>日付</b>で並べています。広告手数料の有無で順位を操作することはありません。仮日程の回は、確定前に予約して損をしないよう、予約リンクを出さず通知登録に切り替えます。</p>
    </div></section>
    <section class="section"><div class="glass">
      <h3>広告・アフィリエイトの表示</h3>
      <p>本サイトは楽天トラベル・じゃらん等のアフィリエイトプログラム（広告）を利用しています。掲載リンク経由で予約・購入があった場合、当サイトが手数料を受け取ることがあります（利用者の支払額は増えません）。景品表示法（ステマ規制）に基づき、広告を含むリンクにはその旨を表示しています。</p>
    </div></section>
    <section class="section"><div class="glass">
      <h3>免責事項</h3>
      <p>料金・空室・便・観覧場所・駐車台数・交通規制は取得時点の参考値で、打ち上げごとに変わります。確定情報は必ず各公式サイトで確認してください。打ち上げは天候・技術要因で延期が頻発します。予備日を2〜3日みて手配することを推奨します。</p>
    </div></section>
    <section class="section">
      <h3 style="margin-bottom:14px;font-size:17px">データ出典</h3>
      <div class="src-grid">
        <div class="glass-sub"><div class="lbl">LAUNCHES</div><p class="ctx">The Space Devs Launch Library 2</p></div>
        <div class="glass-sub"><div class="lbl">HOTELS</div><p class="ctx">楽天トラベル 空室検索API</p></div>
        <div class="glass-sub"><div class="lbl">MAP</div><p class="ctx">Natural Earth（パブリックドメイン）</p></div>
      </div>
    </section>
    <section class="section"><div class="glass" style="display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap">
      <div><h3 style="margin-bottom:4px">お問い合わせ</h3><p style="margin:0">ご要望・不具合の報告はフォームから受け付けています。</p></div>
      <a class="btn btn-primary" href="${CONTACT_FORM}" target="_blank" rel="noopener">お問い合わせフォーム</a>
    </div></section>
    </div>
  `;
}

// ---- バナー（stale/error） --------------------------------------------------

function bannerHtml(): string {
  if (!state.meta) return '';
  const { source, stale, cachedAt } = state.meta;
  if (source === 'error') {
    return `<div class="banner error">⚠️ 打ち上げ情報を取得できませんでした。射場情報は表示できます（外部API不通・時間をおいて再読込してください）。</div>`;
  }
  if (source === 'fallback') {
    // キャッシュではなくアプリに同梱したスナップショット。出所を偽らずそう言う。
    return `<div class="banner warn">⚠️ 最新の打ち上げ情報を取得できないため、${E(fmtCachedAt(cachedAt))}時点の保存データを表示しています。日程は各運用機関の公式発表でご確認ください。</div>`;
  }
  if (stale || source === 'stale') {
    // ⚠️ ここで「stale なら即警告」にしてはいけない。APIの stale は
    //    FRESH_SECONDS(=1時間) を過ぎたという内部フラグで、利用者にとっての古さではない。
    //    1時間で警告していた頃は、有効な12件を表示できているのに
    //    「取得できず」の警告が本番トップに出っぱなしだった（2026-07-31実測）。
    //    どこから古いと言うかの判定は src/lib/launch-cache.ts に集約する。
    const level = cacheAgeLevel(cachedAt, Date.now());
    // 数時間の遅れでは表示内容が変わらないので黙る（取得時刻は一覧見出しの「（…時点）」に出ている）
    if (level === 'current') return '';
    if (level === 'aging') {
      return `<div class="banner note">打ち上げ情報の最終更新は${E(fmtCachedAt(cachedAt))}時点です。直前の変更は各運用機関の公式発表をご確認ください。</div>`;
    }
    return `<div class="banner warn">⚠️ 打ち上げ情報を${E(fmtCachedAt(cachedAt))}時点から更新できていません。日程が変わっている可能性があるため、各運用機関の公式発表をご確認ください。</div>`;
  }
  return '';
}
function fmtCachedAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ============================================================================
// ルーター
// ============================================================================

function parseHash(): Route {
  const h = location.hash.replace(/^#/, '') || '/';
  const [path, qs] = h.split('?');
  const query = new URLSearchParams(qs || '');
  const p = (path || '/').replace(/\/+$/, '') || '/';
  let page: Route['page'] = 'top';
  if (p === '/launches') page = 'launches';
  else if (p === '/arrange') page = 'arrange';
  else if (p === '/guide') page = 'guide';
  else if (p === '/about') page = 'about';
  return { page, query };
}

function render(): void {
  const r = state.route;
  document.body.setAttribute('data-page', r.page);
  // フィルタをURLクエリから復元（launches）
  if (r.page === 'launches') {
    const s = r.query.get('site');
    const c = r.query.get('conf');
    if (s) state.filterSite = s;
    if (c === 'confirmed' || c === 'all') state.filterConf = c;
  }
  /*
   * 手配ページの打ち上げ指定（`#/arrange?launch=<id>`）。
   * 公式LINE・公式Xの通知から「その回」の手配画面を直接開くために要る。
   *
   * ⚠️ ここで解決すること。loadLaunches() は読み込み完了時に
   *    selectedLaunchId を firstBookable() で無条件に上書きするので、
   *    ルーター側で先に入れても消される（render は loadLaunches の最後に呼ばれる）。
   * ⚠️ 未知のidは黙って無視する（＝既定の選択のまま）。存在しない回を指されたときに
   *    真っ白にせず、まず何かが表示される側に倒す。
   */
  if (r.page === 'arrange') {
    const wanted = r.query.get('launch');
    const l = wanted ? launchById(wanted) : null;
    if (l) {
      state.selectedLaunchId = l.id;
      state.selectedSiteId = l.siteId;
    }
  }
  setActiveNav(r.page);
  switch (r.page) {
    case 'top': renderTop(); break;
    case 'launches': renderLaunches(); break;
    case 'arrange': renderArrange(); break;
    case 'guide': renderGuide(); break;
    case 'about': renderAbout(); break;
  }
  renderMobileCta();
  closeMobileNav();
}

function setActiveNav(page: Route['page']): void {
  const active = page === 'arrange' ? 'launches' : page;
  document.querySelectorAll<HTMLElement>('.mainnav a[data-nav]').forEach((a) => {
    a.classList.toggle('is-active', a.dataset.nav === active);
  });
}

function onRoute(): void {
  state.route = parseHash();
  window.scrollTo({ top: 0, behavior: 'auto' });
  render();
}

// ---- スマホ固定CTAバー ------------------------------------------------------

function renderMobileCta(): void {
  const bar = document.getElementById('mobile-cta');
  if (!bar) return;
  const l = launchById(state.selectedLaunchId);
  const site = state.selectedSiteId ? getSiteById(state.selectedSiteId) : null;
  const page = state.route.page;
  let show = false;
  let label = '';
  let value = '';
  let btn = '';
  if (l && site) {
    const d = formatLaunchDate(l.net, l.netPrecision);
    label = '選択中';
    value = `${site.nameJa.replace(/（.*/, '')}・${d.main}`;
    if (page === 'top' || page === 'launches' || page === 'guide') {
      show = true;
      btn = `<a class="btn btn-primary" href="#/arrange">手配をひらく</a>`;
    } else if (page === 'arrange') {
      show = true;
      const p = precisionInfo(l.netPrecision);
      btn = p.bookable
        ? `<button class="btn btn-primary" data-action="scroll" data-target="arr-stay">宿を見る</button>`
        : notifyLink('LINEで通知', 'btn btn-primary');
    }
  }
  if (show) {
    bar.innerHTML = `<div class="mc-info"><div class="mc-label">${E(label)}</div><div class="mc-value">${E(value)}</div></div>${btn}`;
    bar.classList.add('show');
    bar.setAttribute('aria-hidden', 'false');
  } else {
    bar.classList.remove('show');
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = '';
  }
}

function closeMobileNav(): void {
  $('#mainnav')?.classList.remove('open');
  $('#navtoggle')?.setAttribute('aria-expanded', 'false');
}

// ---- 選択 → 手配へ ----------------------------------------------------------

function selectLaunch(id: string): void {
  const l = launchById(id);
  if (!l) return;
  state.selectedLaunchId = id;
  state.selectedSiteId = l.siteId;
  location.hash = '#/arrange';
}
function selectSite(siteId: string): void {
  state.selectedSiteId = siteId;
  const nl = nextLaunchForSite(siteId);
  state.selectedLaunchId = nl?.id ?? null;
  location.hash = '#/arrange';
}

// ---- イベント委譲 -----------------------------------------------------------

document.addEventListener('click', (ev) => {
  const target = ev.target as HTMLElement;
  const nt = target.closest('#navtoggle');
  if (nt) {
    const nav = $('#mainnav');
    const open = nav?.classList.toggle('open');
    nt.setAttribute('aria-expanded', String(!!open));
    return;
  }
  const t = target.closest<HTMLElement>('[data-action]');
  if (!t) return;
  const action = t.dataset.action;
  switch (action) {
    case 'select-launch': selectLaunch(t.dataset.id!); break;
    case 'select-site': selectSite(t.dataset.site!); break;
    case 'guide-site': location.hash = `#/guide?site=${encodeURIComponent(t.dataset.site!)}`; break;
    case 'filter-site': {
      state.filterSite = t.dataset.site!;
      updateLaunchesUrl();
      $('#filters')!.innerHTML = filtersInner();
      $('#launch-list-body')!.innerHTML = launchesBody();
      break;
    }
    case 'filter-conf': {
      state.filterConf = t.dataset.conf === 'confirmed' ? 'confirmed' : 'all';
      updateLaunchesUrl();
      $('#filters')!.innerHTML = filtersInner();
      $('#launch-list-body')!.innerHTML = launchesBody();
      break;
    }
    case 'set-radius': {
      const r = Number(t.dataset.r);
      if (Number.isFinite(r) && r !== state.radiusKm) {
        state.radiusKm = r;
        const l = launchById(state.selectedLaunchId);
        const site = state.selectedSiteId ? getSiteById(state.selectedSiteId) : null;
        if (l && site) {
          const body = document.getElementById('hotel-body');
          if (body) { body.innerHTML = `<p class="loading">空室を再検索中…</p>`; void loadHotels(site, currentTrip(l)); }
        }
      }
      break;
    }
    case 'trip-reset': {
      // 手動で入れた日付を捨てて、打ち上げ日からの自動算出（前日入り〜翌日帰り）に戻す
      clearTripDates();
      refreshTripDependent({ reloadHotels: true });
      break;
    }
    case 'scroll': {
      const el = document.getElementById(t.dataset.target!);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // カテゴリチップの選択状態。チップ以外（本文中の「旅程の条件を変える」やスマホ固定CTA）から
      // 呼ばれたときに選択を消さないよう、チップのときだけ付け替える。
      if (t.classList.contains('chip')) {
        document.querySelectorAll('.cat-chips .chip').forEach((c) => c.classList.remove('is-on'));
        t.classList.add('is-on');
      }
      break;
    }
  }
});

/**
 * 旅程条件（出発地・出発日・帰着日・人数・部屋数）の変更。
 * ★ここが「1回だけ入力する条件」の唯一の書き込み口。
 *   変更は state → localStorage → 依存セクション再描画（宿は日付/人数のときだけ再検索）の順で伝える。
 */
document.addEventListener('change', (ev) => {
  const t = ev.target as (HTMLSelectElement & HTMLInputElement) | null;
  if (!t || !t.id) return;
  const l = launchById(state.selectedLaunchId);

  // ---- 出発地（都道府県／空港）----
  if (t.id === 'dep-pref' || t.id === 'dep-air') {
    if (t.id === 'dep-pref') {
      const pref = findPrefByCode(t.value) ?? findPrefByCode(DEFAULT_PREF_CODE)!;
      state.departurePref = pref.code;
      // 県を変えたら、その県で選べる空港に寄せる（前の県の空港が残らないように）
      state.departureIata = resolveAirport(pref, state.departureIata).iata;
    } else {
      const pref = findPrefByCode(state.departurePref) ?? findPrefByCode(DEFAULT_PREF_CODE)!;
      state.departureIata = resolveAirport(pref, t.value).iata;
    }
    // 自分で選んだ＝以後は現在地の推定で上書きしない（保存するのはこの選択だけ）
    departureOrigin = 'chosen';
    try {
      localStorage.setItem('rvp.departure.pref', state.departurePref);
      localStorage.setItem('rvp.departure', state.departureIata);
    } catch {
      /* noop */
    }
    // 県が変わると空港セレクトの有無が変わるので、出発地ブロックだけ作り直す
    if (t.id === 'dep-pref') {
      const from = document.getElementById('trip-from');
      if (from) from.innerHTML = tripFromFields();
      document.getElementById('dep-pref')?.focus();
    }
    // 宿は出発地に依存しない（射場周辺を探しているだけ）ので再検索しない
    refreshTripDependent({ reloadHotels: false });
    return;
  }

  // ---- 日付 ----
  if (t.id === 'trip-depart' || t.id === 'trip-return') {
    if (!l) return;
    const cur = currentTrip(l);
    const changed = t.id === 'trip-depart' ? 'depart' : 'return';
    const next = clampDates(
      changed === 'depart' ? t.value : cur.departIso,
      changed === 'return' ? t.value : cur.returnIso,
      todayIsoJst(),
      changed,
      launchDayIsoOf(l),
    );
    persistTripDates(l.id, next.departIso, next.returnIso);
    refreshTripDependent({ reloadHotels: true });
    return;
  }

  // ---- 人数・部屋数 ----
  if (t.id === 'trip-adults' || t.id === 'trip-rooms') {
    if (t.id === 'trip-adults') state.adults = clampAdults(t.value);
    else state.rooms = clampRooms(t.value);
    // 1室あたり0人はありえないので、部屋数が人数を超えないよう寄せる（サーバ側の正規化と同じ規則）
    if (state.rooms > state.adults) state.rooms = state.adults;
    try {
      localStorage.setItem('rvp.trip.adults', String(state.adults));
      localStorage.setItem('rvp.trip.rooms', String(state.rooms));
    } catch {
      /* noop */
    }
    const rooms = document.getElementById('trip-rooms') as HTMLSelectElement | null;
    if (rooms && rooms.value !== String(state.rooms)) rooms.value = String(state.rooms);
    refreshTripDependent({ reloadHotels: true });
  }
});

function updateLaunchesUrl(): void {
  const q = new URLSearchParams();
  if (state.filterSite !== 'all') q.set('site', state.filterSite);
  if (state.filterConf !== 'all') q.set('conf', state.filterConf);
  const qs = q.toString();
  const newHash = `#/launches${qs ? '?' + qs : ''}`;
  // URLだけ差し替え（再描画は上で手動）
  history.replaceState(null, '', newHash);
  state.route = parseHash();
}

// ---- 起動 -------------------------------------------------------------------

function boot(): void {
  const foot = $('#global-caveat');
  if (foot) foot.textContent = GLOBAL_CAVEAT;
  state.route = parseHash();
  render(); // 先に骨格を描画（読み込み中表示）
  window.addEventListener('hashchange', onRoute);
  void loadLaunches();
  void detectDeparture(); // 出発地の初期値を接続元から推定（失敗しても既定のまま）
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
